import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LuBellOff, LuCheck, LuCheckCheck } from "react-icons/lu";
import apiClient from "../../api-services/ApiClient";
import AdminLayout from "../../app-components/AdminLayout";
import { NOTIFICATION_TYPES, NotificationIcon, timeAgo } from "../../app-components/header/notificationStyle";
import PageHeader from "../../app-components/ui/PageHeader";
import Pagination from "../../app-components/ui/Pagination";
import { btnPrimary, btnSecondary, card, inputBase } from "../../app-components/ui/styles";
import { AdminNotification, announceNotificationsChanged } from "../../hooks/useAdminNotifications";
import { getErrorMessage } from "../../utils";
import notify from "../../utils/notify";

const Notifications = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<AdminNotification[]>([]);
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(0);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError("");
    try {
      const res: any = await apiClient.get("notifications", { params: { filter, type: type || undefined, page } });
      if (mine !== seq.current) return;
      const body = res?.data ?? {};
      setItems(body.data ?? []);
      setLastPage(Number(body.last_page) || 0);
      setTotal(typeof body.total === "number" ? body.total : undefined);
      setUnread(Number(body.unread_count) || 0);
    } catch (err) {
      if (mine !== seq.current) return;
      setError(getErrorMessage(err));
      setItems([]);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [filter, type, page]);

  useEffect(() => {
    load();
  }, [load]);

  const markRead = async (n: AdminNotification) => {
    setItems((list) => list.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    try {
      const res: any = await apiClient.post(`notifications/${n.id}/read`);
      setUnread(Number(res?.data?.unread_count) || 0);
      announceNotificationsChanged();
      if (filter === "unread") load();
    } catch (err) {
      notify.error(`Couldn't mark it as read: ${getErrorMessage(err)}`);
      load();
    }
  };

  const markAll = async () => {
    try {
      const res: any = await apiClient.post("notifications/read-all");
      notify.success(`Marked ${Number(res?.data?.marked) || 0} as read.`);
      announceNotificationsChanged();
      setPage(1);
      load();
    } catch (err) {
      notify.error(`Couldn't mark all as read: ${getErrorMessage(err)}`);
    }
  };

  const open = (n: AdminNotification) => {
    if (!n.read) markRead(n);
    if (n.link) navigate(n.link);
  };

  const tabs: { value: "all" | "unread"; label: string }[] = [
    { value: "all", label: "All" },
    { value: "unread", label: unread ? `Unread (${unread})` : "Unread" },
  ];

  return (
    <AdminLayout title="Admin | Notifications">
      <PageHeader
        title="Notifications"
        subtitle="Things that need an admin's attention, and changes made by other admins."
        actions={
          <button type="button" onClick={markAll} disabled={!unread} className={`${btnSecondary} inline-flex items-center gap-2`}>
            <LuCheckCheck className="h-4 w-4" aria-hidden />
            Mark all as read
          </button>
        }
      />

      <div className={`${card} py-5`}>
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3 border-b border-hcBorder px-5">
          <div className="flex items-center gap-1">
            {tabs.map((t) => (
              <button
                key={t.value}
                onClick={() => {
                  setFilter(t.value);
                  setPage(1);
                }}
                className={`-mb-px border-b-2 px-4 pb-3 text-sm font-medium transition ${
                  filter === t.value
                    ? "border-honeConnectPurple text-honeConnectPurple"
                    : "border-transparent text-honeConnectGray hover:text-honeConnectBlack"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <select
            aria-label="Type"
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              setPage(1);
            }}
            className={`${inputBase} mb-3 py-2`}
          >
            <option value="">All types</option>
            {NOTIFICATION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {error && !loading ? (
          <div className="flex flex-col items-center px-6 py-14 text-center">
            <p className="mb-1 font-semibold text-honeConnectBlack">Couldn't load notifications</p>
            <p className="mb-4 max-w-md text-sm text-hcMuted">{error}</p>
            <button onClick={load} className={btnPrimary}>
              Try again
            </button>
          </div>
        ) : loading && items.length === 0 ? (
          <ul className="space-y-4 px-5 py-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <li key={i} className="flex items-center gap-3">
                <span className="h-9 w-9 animate-pulse rounded-full bg-hcBg" />
                <span className="h-3 flex-1 animate-pulse rounded bg-hcBg" />
              </li>
            ))}
          </ul>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center px-6 py-14 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-hcBg text-hcMuted">
              <LuBellOff className="h-5 w-5" aria-hidden />
            </div>
            <p className="font-semibold text-honeConnectBlack">
              {filter === "unread" ? "No unread notifications" : "No notifications yet"}
            </p>
            <p className="mt-1 max-w-sm text-sm text-hcMuted">
              {filter === "unread"
                ? "You're all caught up."
                : "New elite applications, refused payments and changes by other admins will appear here."}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-hcBorder border-y border-hcBorder">
            {items.map((n) => (
              <li key={n.id} className={`flex items-start gap-3 px-5 py-4 ${n.read ? "" : "bg-hc_F4EBFA/40"}`}>
                <NotificationIcon type={n.type} />
                <button type="button" onClick={() => open(n)} className="min-w-0 flex-1 text-left focus:outline-none">
                  <p className={`text-sm ${n.read ? "text-honeConnectGray" : "font-medium text-honeConnectBlack"}`}>{n.title}</p>
                  {n.body && <p className="mt-0.5 text-sm text-hcMuted">{n.body}</p>}
                  <p className="mt-1 text-xs text-hcMuted">
                    {timeAgo(n.created_at)}
                    {n.link && <span className="ml-2 font-medium text-honeConnectPurple">Open</span>}
                  </p>
                </button>
                {!n.read && (
                  <button
                    type="button"
                    onClick={() => markRead(n)}
                    title="Mark as read"
                    aria-label={`Mark "${n.title}" as read`}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-honeConnectGray hover:bg-white hover:text-honeConnectPurple"
                  >
                    <LuCheck className="h-4 w-4" aria-hidden />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {items.length > 0 && lastPage > 1 && (
          <Pagination currentPage={page} lastPage={lastPage} total={total} onPageChange={setPage} />
        )}
      </div>
    </AdminLayout>
  );
};

export default Notifications;
